import { useState, useEffect } from "react";
import axios from "axios";
import { API_URL, getAuthHeaders, ROUTE_ORDERS } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { CheckpointMap } from "../components/Map/CheckpointMap";
import { Route as RouteIcon, Plus, Pencil, Trash2, MapPin, Users, Shuffle, ListOrdered, ArrowUp, ArrowDown, GripVertical, LayoutGrid, Table as TableIcon } from "lucide-react";
import { toast } from "sonner";

export const Routes = () => {
  const { user, isAdmin, isRevendedor } = useAuth();
  const [routes, setRoutes] = useState([]);
  const [checkpoints, setCheckpoints] = useState([]);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [viewMode, setViewMode] = useState("tiles"); // "tiles" or "grid"
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    company_id: "",
    checkpoint_ids: [],
    order_type: "sequential",
    assigned_user_ids: [],
  });
  const [draggedIndex, setDraggedIndex] = useState(null);

  const fetchData = async (companyId = null) => {
    try {
      setLoading(true);
      const routesUrl = companyId && companyId !== "all" 
        ? `${API_URL}/routes?company_id=${companyId}`
        : `${API_URL}/routes`;
      
      const [routesRes, checkpointsRes, usersRes, companiesRes] = await Promise.all([
        axios.get(routesUrl, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/checkpoints`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/users`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/companies`, { headers: getAuthHeaders() }),
      ]);
      setRoutes(Array.isArray(routesRes.data) ? routesRes.data : []);
      setCheckpoints(Array.isArray(checkpointsRes.data) ? checkpointsRes.data : []);
      const usersData = Array.isArray(usersRes.data) ? usersRes.data : [];
      setUsers(usersData.filter((u) => u.role === "vigilante"));
      setCompanies(Array.isArray(companiesRes.data) ? companiesRes.data : []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar datos");
      setRoutes([]);
      setCheckpoints([]);
      setUsers([]);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(selectedCompany);
  }, [selectedCompany]);

  const handleOpenForm = (route = null) => {
    if (route) {
      setSelectedRoute(route);
      setFormData({
        name: route.name,
        company_id: route.company_id,
        checkpoint_ids: route.checkpoint_ids || [],
        order_type: route.order_type || "sequential",
        assigned_user_ids: route.assigned_user_ids || [],
      });
    } else {
      setSelectedRoute(null);
      setFormData({
        name: "",
        company_id: isAdmin() ? "" : user.company_id,
        checkpoint_ids: [],
        order_type: "sequential",
        assigned_user_ids: [],
      });
    }
    setIsFormOpen(true);
  };

  const handleCheckpointToggle = (checkpointId) => {
    const current = formData.checkpoint_ids;
    if (current.includes(checkpointId)) {
      setFormData({
        ...formData,
        checkpoint_ids: current.filter((id) => id !== checkpointId),
      });
    } else {
      setFormData({
        ...formData,
        checkpoint_ids: [...current, checkpointId],
      });
    }
  };

  const moveCheckpoint = (index, direction) => {
    const newOrder = [...formData.checkpoint_ids];
    if (direction === 'up' && index > 0) {
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    } else if (direction === 'down' && index < newOrder.length - 1) {
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    }
    setFormData({
      ...formData,
      checkpoint_ids: newOrder,
    });
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const newOrder = [...formData.checkpoint_ids];
    const draggedItem = newOrder[draggedIndex];
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(dropIndex, 0, draggedItem);

    setFormData({
      ...formData,
      checkpoint_ids: newOrder,
    });
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const getSelectedCheckpointsInOrder = () => {
    if (!Array.isArray(checkpoints)) return [];
    return formData.checkpoint_ids
      .map(id => checkpoints.find(cp => cp.id === id))
      .filter(Boolean);
  };

  const handleUserToggle = (userId) => {
    const current = formData.assigned_user_ids;
    if (current.includes(userId)) {
      setFormData({
        ...formData,
        assigned_user_ids: current.filter((id) => id !== userId),
      });
    } else {
      setFormData({
        ...formData,
        assigned_user_ids: [...current, userId],
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.checkpoint_ids.length === 0) {
      toast.error("Selecciona al menos un punto de control");
      return;
    }
    try {
      if (selectedRoute) {
        await axios.put(`${API_URL}/routes/${selectedRoute.id}`, formData, {
          headers: getAuthHeaders(),
        });
        toast.success("Ruta actualizada");
      } else {
        await axios.post(`${API_URL}/routes`, formData, {
          headers: getAuthHeaders(),
        });
        toast.success("Ruta creada");
      }
      setIsFormOpen(false);
      fetchData(selectedCompany);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/routes/${selectedRoute.id}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Ruta eliminada");
      setIsDeleteOpen(false);
      setSelectedRoute(null);
      fetchData(selectedCompany);
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const getRouteCheckpoints = (route) => {
    if (!Array.isArray(checkpoints)) return [];
    return checkpoints.filter((cp) => route.checkpoint_ids?.includes(cp.id));
  };

  const getAssignedUsers = (route) => {
    if (!Array.isArray(users)) return [];
    return users.filter((u) => route.assigned_user_ids?.includes(u.id));
  };

  const filteredCheckpoints = formData.company_id
    ? (Array.isArray(checkpoints) ? checkpoints.filter((cp) => cp.company_id === formData.company_id) : [])
    : (Array.isArray(checkpoints) ? checkpoints : []);

  // Para revendedores, mostrar todos los vigilantes de sus compañías asignadas
  // Para otros roles, filtrar por la compañía seleccionada
  const filteredUsers = (() => {
    if (!Array.isArray(users)) return [];
    
    // Si es revendedor, mostrar todos los vigilantes disponibles (ya filtrados por backend)
    if (user?.role === "revendedor") {
      return users;
    }
    
    // Para otros roles, filtrar por company_id si está seleccionada
    if (formData.company_id) {
      return users.filter((u) => u.company_id === formData.company_id);
    }
    
    return users;
  })();

  return (
    <div className="space-y-6" data-testid="routes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Rutas</h1>
          <p className="text-slate-600 mt-1">
            Configura las rutas de patrullaje para vigilantes
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Filtro por compañía - Solo para admin y revendedor */}
          {(isAdmin() || isRevendedor()) && (
            <Select
              value={selectedCompany}
              onValueChange={(value) => {
                setSelectedCompany(value);
                setLoading(true);
              }}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filtrar por compañía" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las compañías</SelectItem>
                {companies.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {/* Switch de vista */}
          <div className="flex items-center gap-2 px-3 py-2 border rounded-md">
            <LayoutGrid className={`h-4 w-4 ${viewMode === "tiles" ? "text-primary" : "text-slate-400"}`} />
            <Switch
              checked={viewMode === "grid"}
              onCheckedChange={(checked) => setViewMode(checked ? "grid" : "tiles")}
            />
            <TableIcon className={`h-4 w-4 ${viewMode === "grid" ? "text-primary" : "text-slate-400"}`} />
          </div>
          <Button onClick={() => handleOpenForm()} data-testid="add-route-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nueva Ruta
          </Button>
        </div>
      </div>

      {/* Routes List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : !Array.isArray(routes) || routes.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <RouteIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No hay rutas configuradas</p>
            <Button className="mt-4" onClick={() => handleOpenForm()}>
              Crear primera ruta
            </Button>
          </CardContent>
        </Card>
      ) : viewMode === "tiles" ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {routes.map((route) => {
            const routeCheckpoints = getRouteCheckpoints(route);
            const assignedUsers = getAssignedUsers(route);
            return (
              <Card key={route.id} className="overflow-hidden">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                        {route.order_type === "random" ? (
                          <Shuffle className="h-5 w-5 text-white" />
                        ) : (
                          <ListOrdered className="h-5 w-5 text-white" />
                        )}
                      </div>
                      <div>
                        <CardTitle className="text-lg">{route.name}</CardTitle>
                        <p className="text-sm text-slate-500 capitalize">
                          {route.order_type === "random" ? "Aleatorio" : "Secuencial"}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenForm(route)}
                        data-testid={`edit-route-${route.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => {
                          setSelectedRoute(route);
                          setIsDeleteOpen(true);
                        }}
                        data-testid={`delete-route-${route.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Map Preview */}
                  <CheckpointMap
                    checkpoints={routeCheckpoints}
                    height="200px"
                    interactive={false}
                    showRadius={false}
                  />

                  {/* Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-slate-400" />
                      <span className="text-slate-600">
                        {routeCheckpoints.length} puntos
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="h-4 w-4 text-slate-400" />
                      <span className="text-slate-600">
                        {assignedUsers.length} asignados
                      </span>
                    </div>
                  </div>

                  {/* Assigned Users */}
                  {assignedUsers.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {assignedUsers.map((u) => (
                        <span
                          key={u.id}
                          className="inline-flex items-center px-2 py-1 bg-slate-100 text-slate-700 rounded-full text-xs"
                        >
                          {u.name}
                        </span>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Puntos</TableHead>
                    <TableHead>Vigilantes</TableHead>
                    <TableHead>Vigilantes Asignados</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {routes.map((route) => {
                    const routeCheckpoints = getRouteCheckpoints(route);
                    const assignedUsers = getAssignedUsers(route);
                    return (
                      <TableRow key={route.id}>
                        <TableCell className="font-medium">{route.name}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {route.order_type === "random" ? (
                              <>
                                <Shuffle className="h-4 w-4 text-slate-400" />
                                <span className="capitalize">Aleatorio</span>
                              </>
                            ) : (
                              <>
                                <ListOrdered className="h-4 w-4 text-slate-400" />
                                <span className="capitalize">Secuencial</span>
                              </>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-slate-400" />
                            <span>{routeCheckpoints.length}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-slate-400" />
                            <span>{assignedUsers.length}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {assignedUsers.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {assignedUsers.slice(0, 3).map((u) => (
                                <span
                                  key={u.id}
                                  className="inline-flex items-center px-2 py-1 bg-slate-100 text-slate-700 rounded-full text-xs"
                                >
                                  {u.name}
                                </span>
                              ))}
                              {assignedUsers.length > 3 && (
                                <span className="inline-flex items-center px-2 py-1 bg-slate-100 text-slate-700 rounded-full text-xs">
                                  +{assignedUsers.length - 3}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenForm(route)}
                              data-testid={`edit-route-${route.id}`}
                            >
                              <Pencil className="h-3 w-3 mr-1" />
                              Editar
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => {
                                setSelectedRoute(route);
                                setIsDeleteOpen(true);
                              }}
                              data-testid={`delete-route-${route.id}`}
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              Eliminar
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedRoute ? "Editar Ruta" : "Nueva Ruta"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre de la Ruta *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Ruta Norte"
                required
                data-testid="route-name-input"
              />
            </div>

            {/* Solo admin ve selector de compañía */}
            {isAdmin() ? (
              <div className="space-y-2">
                <Label>Compañía *</Label>
                <Select
                  value={formData.company_id}
                  onValueChange={(value) =>
                    setFormData({ ...formData, company_id: value, checkpoint_ids: [], assigned_user_ids: [] })
                  }
                >
                  <SelectTrigger data-testid="route-company-select">
                    <SelectValue placeholder="Seleccionar compañía" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.isArray(companies) && companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            <div className="space-y-2">
              <Label>Tipo de Recorrido</Label>
              <Select
                value={formData.order_type}
                onValueChange={(value) => setFormData({ ...formData, order_type: value })}
              >
                <SelectTrigger data-testid="route-order-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROUTE_ORDERS.map((order) => (
                    <SelectItem key={order.value} value={order.value}>
                      {order.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                {formData.order_type === "random"
                  ? "Los puntos se presentarán en orden aleatorio"
                  : "Los puntos se recorrerán en el orden seleccionado"}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Puntos de Control * ({formData.checkpoint_ids.length} seleccionados)</Label>
              
              {/* Mostrar checkpoints seleccionados (con orden para secuencial, sin orden para aleatorio) */}
              {formData.checkpoint_ids.length > 0 && (
                <div className="mb-4 space-y-2">
                  <Label className="text-sm font-medium text-slate-700">
                    {formData.order_type === "sequential" 
                      ? "Orden de Registro (Arrastra para reordenar o usa las flechas)" 
                      : "Puntos Seleccionados (Se presentarán en orden aleatorio)"}
                  </Label>
                  <div className="border rounded-lg p-3 space-y-2 bg-slate-50">
                    {getSelectedCheckpointsInOrder().map((cp, index) => (
                      <div
                        key={cp.id}
                        draggable={formData.order_type === "sequential"}
                        onDragStart={formData.order_type === "sequential" ? () => handleDragStart(index) : undefined}
                        onDragOver={formData.order_type === "sequential" ? (e) => handleDragOver(e, index) : undefined}
                        onDrop={formData.order_type === "sequential" ? (e) => handleDrop(e, index) : undefined}
                        onDragEnd={formData.order_type === "sequential" ? handleDragEnd : undefined}
                        className={`flex items-center gap-2 p-2 bg-white rounded border transition-all ${
                          formData.order_type === "sequential" 
                            ? `cursor-move ${
                                draggedIndex === index
                                  ? "border-blue-500 bg-blue-50 opacity-50"
                                  : "border-slate-200 hover:border-slate-300 hover:shadow-sm"
                              }`
                            : "border-slate-200"
                        }`}
                      >
                        {formData.order_type === "sequential" && (
                          <GripVertical className="h-4 w-4 text-slate-400 cursor-grab active:cursor-grabbing" />
                        )}
                        <span className="flex-1 text-sm font-medium">
                          {formData.order_type === "sequential" ? `${index + 1}. ` : ""}{cp.name}
                        </span>
                        <div className="flex gap-1">
                          {formData.order_type === "sequential" && (
                            <>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  moveCheckpoint(index, 'up');
                                }}
                                disabled={index === 0}
                                title="Mover arriba"
                              >
                                <ArrowUp className="h-3 w-3" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  moveCheckpoint(index, 'down');
                                }}
                                disabled={index === formData.checkpoint_ids.length - 1}
                                title="Mover abajo"
                              >
                                <ArrowDown className="h-3 w-3" />
                              </Button>
                            </>
                          )}
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCheckpointToggle(cp.id);
                            }}
                            title="Quitar"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Lista de checkpoints disponibles para seleccionar */}
              <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                {filteredCheckpoints.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-2">
                    No hay puntos disponibles
                  </p>
                ) : (
                  filteredCheckpoints
                    .filter(cp => !formData.checkpoint_ids.includes(cp.id))
                    .map((cp) => (
                    <div key={cp.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`cp-${cp.id}`}
                        checked={formData.checkpoint_ids.includes(cp.id)}
                        onCheckedChange={() => handleCheckpointToggle(cp.id)}
                        data-testid={`checkpoint-checkbox-${cp.id}`}
                      />
                      <label
                        htmlFor={`cp-${cp.id}`}
                        className="text-sm font-medium cursor-pointer"
                      >
                        {cp.name}
                      </label>
                    </div>
                  ))
                )}
                {formData.checkpoint_ids.length > 0 && (
                  <p className="text-xs text-slate-500 mt-2 pt-2 border-t border-slate-200">
                    {formData.order_type === "sequential"
                      ? "Los checkpoints seleccionados aparecen arriba en el orden de registro"
                      : "Los checkpoints seleccionados aparecen arriba. Se presentarán en orden aleatorio durante el patrullaje"}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Vigilantes Asignados ({formData.assigned_user_ids.length} seleccionados)</Label>
              <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                {filteredUsers.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-2">
                    No hay vigilantes disponibles
                  </p>
                ) : (
                  filteredUsers.map((u) => (
                    <div key={u.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`user-${u.id}`}
                        checked={formData.assigned_user_ids.includes(u.id)}
                        onCheckedChange={() => handleUserToggle(u.id)}
                        data-testid={`user-checkbox-${u.id}`}
                      />
                      <label
                        htmlFor={`user-${u.id}`}
                        className="text-sm font-medium cursor-pointer"
                      >
                        {u.name} ({u.email})
                      </label>
                    </div>
                  ))
                )}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-route-btn">
                {selectedRoute ? "Guardar Cambios" : "Crear Ruta"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar ruta?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente la
              ruta "{selectedRoute?.name}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-route"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
